import { assert, undefinedToDefault } from "@core/prelude"
import { Guid } from "guid-typescript"
import { OpportunityPropertyDefinition } from "../../../models/opportunity-property-definition"
import { Answer, Choice, Question, Questionnaire, QuestionType, FlowRule } from "./questionnaire"
import { flattenCollection } from "../../../services/contract"

export type ChoiceContract = {
  id: string
  choiceNumber: OpportunityPropertyDefinition<number>
  text: OpportunityPropertyDefinition<string>
  exclusive: OpportunityPropertyDefinition<boolean>
  additionalInformationLabel: OpportunityPropertyDefinition<string>
}

export type QuestionContract = {
  questionNumber: OpportunityPropertyDefinition<number>
  label: OpportunityPropertyDefinition<string>
  choices: { [ id: string]: ChoiceContract }
  questionType: OpportunityPropertyDefinition<QuestionType>
}

export type AnswerContract = {
  choices: OpportunityPropertyDefinition<string[]>
  additionalInformation: OpportunityPropertyDefinition<string>
}

export type QuestionWithAnswerContract = {
  id: string
  question: QuestionContract
  answer: AnswerContract
}

export type QuestionnaireContract = {
  questionnaireId: OpportunityPropertyDefinition<Guid>
  name: OpportunityPropertyDefinition<string>
  questions: { [id: string]: QuestionWithAnswerContract }
  version: OpportunityPropertyDefinition<string>
  flowRules?: FlowRule[]
}


export function questionnaireContractToDomain(contract: QuestionnaireContract): Questionnaire {
  function convertAnswer(answerContract: AnswerContract): Answer {
    return {
      choices: answerContract.choices.value ?? [],
      additionalInformation: answerContract.additionalInformation.value ?? null
    }
  }

  function convertQuestions(qs: { [ id: string]: QuestionWithAnswerContract }) : Question[] {
    return flattenCollection<QuestionWithAnswerContract>(qs).map((q) => {
      assert(q.question.label.value != undefined, "The question label cannot be undefined.")
      assert(q.question.questionType.value != undefined, "The question type cannot be undefined.")
      assert(q.question.questionNumber.value != undefined, "The question number cannot be undefined.")
      return {
        id: q.id,
        questionNumber: q.question.questionNumber.value,
        label: q.question.label.value,
        questionType: q.question.questionType.value,
        answer: convertAnswer(q.answer),
        options: flattenCollection<ChoiceContract>(q.question.choices)
          .map((c, choiceIndex) => {
            assert(c.text.value != undefined, 'The question text cannot be undefined.')
            assert(c.exclusive.value != undefined, 'The question exlusive flag cannot be undefined.')
            assert(c.choiceNumber.value != undefined, 'The question choice number cannot be undefined.')
            return {
              id: c.id,
              choiceNumber: c.choiceNumber.value,
              choice: c.text.value,
              exclusive: c.exclusive.value,
              freeTextLabel: undefinedToDefault(c.additionalInformationLabel.value, null)
            }
          })
          .sort((a, b) => a.choiceNumber - b.choiceNumber), // Sort by choice number
      };
    });
  };
  
  assert(contract.questionnaireId.value != undefined, 'The questionnaireId cannot be undefined.');
  assert(contract.name.value, 'The questionnaire name cannot be undefined.');
  assert(contract.version.value, 'The questionnaire version cannot be undefined.');

  return  {
    questionnaireId: contract.questionnaireId.value,
    name: contract.name.value,
    version: contract.version.value,
    questions: convertQuestions(contract.questions),
    flowRules: contract.flowRules || []
  };
}
